"use client";

/** Desktop and mobile navigation with wallet and account controls. */
import { IconButton } from "@decibeltrade/web-core/components";
import { focusRing } from "@decibeltrade/web-core/recipes";
import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { css, cx } from "styled-system/css";
import { flex } from "styled-system/patterns";

import type { HeaderBackgroundConfig } from "@/app/(main)/background";
import { WalletSelector } from "@/components/wallet-selector";
import { useAppHealthState } from "@/contexts/health";
import { useAuthState } from "@/hooks/use-auth-state";

import {
  DesktopAccountControls,
  MobileAccountControls,
} from "../account-controls/account-controls";
import {
  DesktopSkeletonControls,
  MobileSkeletonControls,
} from "../account-controls/skeleton-controls";
import { Drawer } from "../drawer";
import { MediaQuery } from "../media-query";
import { DecibelLogoWordmark } from "../vectors/decibel-logo-wordmark";
import { AdvancedSettings } from "./advanced-settings/advanced-settings";
import { DesktopNavRow } from "./desktop-nav";
import { MobileNavDrawer } from "./mobile-nav";
import { NavMenuIcon } from "./nav-menu-icon";

// Lazy-load MobileTradeHeader so the trade-only graph (MarketSelectorMobile,
// MarketsTableMobile, watch-toggle, etc.) doesn't ride in the shared (main)-
// layout chunk that every non-trade page loads. We keep SSR enabled so the
// mobile /trade page paints a real header instead of a hydration-time blank.
const MobileTradeHeader = dynamic(() =>
  import("@/app/(main)/trade/[[...market-name]]/mobile-header").then((m) => m.MobileTradeHeader),
);

const SCROLL_FADE_END = "20vh"; // Header fades in over first 20vh of scroll

/**
 * On /points:
 * - Fallback browsers: solid flat background with static blur
 * - Supporting browsers: smooth fade-in over first 20vh scroll, using ::before layer, and animated blur
 */
const backgroundFadeClass = css({
  "@supports (animation-timeline: scroll())": {
    backgroundColor: "transparent",
    transition: "[none!]",
    _before: {
      content: '""',
      position: "absolute",
      inset: "0",
      pointerEvents: "none",
      zIndex: "[-1]",
      background: "background.canvas",
      opacity: "0",
      animation: "headerFade linear both",
      animationTimeline: "scroll(root block)",
      animationRange: `0 ${SCROLL_FADE_END}`,
    },
  },
});

const ANIMATION_STYLES = `
  @keyframes headerFade {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes headerBlur {
    from { backdrop-filter: blur(0px); }
    to { backdrop-filter: blur(12px); }
  }
  @supports (animation-timeline: scroll()) {
    .header-blur {
      backdrop-filter: blur(0px);
      animation: headerBlur linear both;
      animation-timeline: scroll(root block);
      animation-range: 0 ${SCROLL_FADE_END};
    }
  }
  @supports not (animation-timeline: scroll()) {
    .header-blur {
      backdrop-filter: blur(12px);
    }
  }
`;

function getHeaderStyle(hasBgBlur: boolean, background?: string): React.CSSProperties {
  if (background === "transparent") return { backdropFilter: "blur(12px)" };
  if (hasBgBlur) return {}; // Blur animated via CSS class
  return background ? { background } : {};
}

export function AppHeader({ background }: { background?: HeaderBackgroundConfig }) {
  const desktopBg = typeof background === "string" ? background : background?.desktop;
  const mobileBg = typeof background === "string" ? background : background?.mobile;

  return (
    <>
      <style>{ANIMATION_STYLES}</style>
      <div data-app-header="" className={headerWrapperStyles}>
        <DesktopNav background={desktopBg} />
        <MobileNav background={mobileBg} />
      </div>
    </>
  );
}

interface NavProps {
  background?: string;
}

function DesktopNav({ background }: NavProps) {
  const t = useTranslations("navItems");
  const pathname = usePathname();
  const hasBgBlur = pathname.startsWith("/points");

  return (
    <MediaQuery display="above" breakpoint="desktop">
      {(className) => (
        <header
          className={cx(
            flex({
              align: "center",
              gap: { base: "16", lg: "32" },
              w: "full",
              px: "20",
              pt: "12",
              pb: "8",
              position: "relative",
              bg: "background.canvas",
            }),
            hasBgBlur && backgroundFadeClass,
            hasBgBlur && "header-blur",
            className,
          )}
          style={getHeaderStyle(hasBgBlur, background)}
        >
          <MediaQuery display="below" breakpoint="lg">
            {(className) => (
              <div className={className}>
                <Drawer
                  trigger={
                    <IconButton
                      ariaLabel={t("openNavigationMenu")}
                      size="lg"
                      variant="text"
                      className={css({ p: "8!", m: "-8" })}
                    >
                      <NavMenuIcon halo="canvas" />
                    </IconButton>
                  }
                  renderContent={({ close }) => <MobileNavDrawer close={close} />}
                  mountOnlyWhenVisible
                />
              </div>
            )}
          </MediaQuery>
          <Link href="/" className={cx(focusRing(), css({ mr: "auto" }))}>
            <DecibelLogoWordmark className={css({ color: "white" })} />
          </Link>
          <MediaQuery display="above" breakpoint="lg">
            {(className) => <DesktopNavRow className={className} />}
          </MediaQuery>
          <div className={flex({ align: "center", gap: "24" })}>
            <DesktopHeaderActions />
            <AdvancedSettings />
          </div>
        </header>
      )}
    </MediaQuery>
  );
}

const DesktopHeaderActions = () => {
  const { state } = useAuthState();

  if (state === "LOADING") {
    return <DesktopSkeletonControls />;
  }

  if (state === "WALLET_CONNECTED" || state === "SESSION_KEY_IMPORTED") {
    return <DesktopAccountControls />;
  }

  return <WalletSelector />;
};

function MobileNav({ background }: NavProps) {
  const t = useTranslations("navItems");
  const pathname = usePathname();
  const isTradePage = pathname.startsWith("/trade");
  const hasBgBlur = pathname.startsWith("/points");
  const isMissingMarkets = useAppHealthState((state) => state.isMissingMarkets);

  if (isTradePage && !isMissingMarkets) return <MobileTradeHeader />;

  return (
    <MediaQuery display="below" breakpoint="desktop">
      {(className) => (
        <header
          className={cx(
            flex({
              position: "relative",
              bg: "background.canvas",
              w: "full",
              align: "center",
              gap: "8",
              px: "16",
              py: "8",
            }),
            hasBgBlur && backgroundFadeClass,
            hasBgBlur && "header-blur",
            className,
          )}
          style={getHeaderStyle(hasBgBlur, background)}
        >
          <Drawer
            trigger={
              <IconButton
                ariaLabel={t("openNavigationMenu")}
                size="lg"
                variant="text"
                className={css({ p: "8!", m: "-8" })}
              >
                <NavMenuIcon halo="canvas" />
              </IconButton>
            }
            renderContent={({ close }) => <MobileNavDrawer close={close} />}
            mountOnlyWhenVisible
          />
          <MobileHeaderActions />
        </header>
      )}
    </MediaQuery>
  );
}

function MobileHeaderActions() {
  const { state } = useAuthState();

  if (state === "LOADING") {
    return <MobileSkeletonControls />;
  }

  if (state === "WALLET_CONNECTED" || state === "SESSION_KEY_IMPORTED") {
    return <MobileAccountControls />;
  }

  return (
    <div className={flex({ flex: "1", justify: "flex-end" })}>
      <WalletSelector />
    </div>
  );
}

const headerWrapperStyles = css({
  position: "sticky",
  top: "0",
  zIndex: "banner",
});
